import React, { useState } from "react";
import { useCart } from "./CartContext";
import { useNavigate } from "react-router-dom";
import { Lock, Loader2 } from "lucide-react";
import { getAuthToken } from "../utils/authToken";

const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY_ID || "";
const FREE_SHIPPING_PRODUCT_IDS = [7];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const existing = document.getElementById("razorpay-checkout-js");
    if (existing) {
      existing.addEventListener("load",  () => resolve(!!window.Razorpay));
      existing.addEventListener("error", () => resolve(false));
      return;
    }
    const s = document.createElement("script");
    s.id     = "razorpay-checkout-js";
    s.src    = "https://checkout.razorpay.com/v1/checkout.js";
    s.async  = true;
    s.onload  = () => resolve(!!window.Razorpay);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function RazorpayButton({ address, amount, disabled }) {
  const { cart, total, clearCart } = useCart();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [errMsg,     setErrMsg]     = useState("");

  const allFreeShipping = cart.length > 0 && cart.every(
    i => i.product?.freeShipping || FREE_SHIPPING_PRODUCT_IDS.includes(Number(i.product_id))
  );
  const shipping = allFreeShipping || total >= 99900 ? 0 : 9900;
  const payable  = typeof amount === "number" ? amount : total + shipping;

  const validateAddress = () => {
    if (!address.name || !address.phone || !address.line1 || !address.city || !address.state || !address.pincode) {
      setErrMsg("Please fill in all required address fields before proceeding.");
      return false;
    }
    if (String(address.phone).replace(/\D/g, "").length !== 10) {
      setErrMsg("Please enter a valid 10-digit phone number.");
      return false;
    }
    if (String(address.pincode).replace(/\D/g, "").length !== 6) {
      setErrMsg("Please enter a valid 6-digit pincode.");
      return false;
    }
    setErrMsg("");
    return true;
  };

  const handlePayment = async () => {
    setErrMsg("");
    if (cart.length === 0) return;
    if (!validateAddress()) return;
    if (!RAZORPAY_KEY) {
      setErrMsg("Payments are not configured yet. Contact support.");
      return;
    }

    setProcessing(true);

    const [ok, token] = await Promise.all([
      loadRazorpayScript(),
      getAuthToken(),
    ]);

    if (!ok || !window.Razorpay) {
      setProcessing(false);
      setErrMsg("Could not load the payment gateway. Check your connection and try again.");
      return;
    }
    if (!token) {
      setProcessing(false);
      setErrMsg("Session expired. Please refresh the page and try again.");
      return;
    }

    // ── Create Razorpay order server-side (required for signature verification) ──
    let rzpOrderId;
    try {
      const ctrl = new AbortController();
      const t    = setTimeout(() => ctrl.abort(), 10000);
      const res  = await fetch("/api/payments/razorpay-order", {
        method:  "POST",
        signal:  ctrl.signal,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        // SEC-5: send items — server derives total from D1, not client amount
        body:    JSON.stringify({
          items: cart.map(i => ({ product_id: i.product_id, quantity: i.quantity })),
        }),
      });
      clearTimeout(t);
      const data = await res.json();
      if (!res.ok || !data.id) throw new Error(data.error || "Could not initiate payment");
      rzpOrderId = data.id;
    } catch (err) {
      setProcessing(false);
      setErrMsg(err.name === "AbortError"
        ? "Payment gateway timed out. Please try again."
        : err.message || "Could not initiate payment. Please try again.");
      return;
    }

    const phone = String(address.phone).replace(/\D/g, "");

    const options = {
      key:         RAZORPAY_KEY,
      amount:      payable,
      currency:    "INR",
      order_id:    rzpOrderId,          // ← links payment to server-side order
      name:        "Ebenezer Books",
      description: `Order of ${cart.length} item${cart.length !== 1 ? "s" : ""}`,
      image:       "/logo.png",

      handler: async function (response) {
        const gatewayPaymentId = response.razorpay_payment_id;
        const gatewayOrderId   = response.razorpay_order_id;
        const gatewaySignature = response.razorpay_signature;

        try {
          // Save order — server verifies signature + checks stock
          const ctrl = new AbortController();
          const fetchTimer = setTimeout(() => ctrl.abort(), 20000);
          let orderRes;
          try {
            orderRes = await fetch("/api/orders/create", {
              method:  "POST",
              signal:  ctrl.signal,
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({
                items: cart.map(i => ({
                  product_id: i.product_id,
                  title:      i.product.title,
                  author:     i.product.author,
                  price:      i.product.price,
                  quantity:   i.quantity,
                })),
                address,
                total:             payable,
                payment_id:        gatewayPaymentId,
                gateway_order_id:  gatewayOrderId,
                gateway_signature: gatewaySignature,
                payment_gateway:   "razorpay",
              }),
            });
          } finally {
            clearTimeout(fetchTimer);
          }

          let orderData;
          try { orderData = await orderRes.json(); }
          catch { throw new Error(`Server error (HTTP ${orderRes.status}). Please contact support.`); }

          if (!orderRes.ok || orderData.error) {
            throw new Error(orderData.error || `Order save failed (HTTP ${orderRes.status}).`);
          }

          // payments/create is no longer needed — orders/create handles everything
          await clearCart();
          navigate("/order-success", {
            state: {
              orderId:   orderData.orderId,
              paymentId: gatewayPaymentId,
              address,
              items:     cart,
              total:     payable,
            },
          });
        } catch (err) {
          console.error("[payment] post-payment error:", err);
          setProcessing(false);
          const isStock = err.message?.toLowerCase().includes("stock");
          setErrMsg(
            isStock
              ? err.message
              : `Payment was received (ID: ${gatewayPaymentId}) but we had trouble saving your order. ` +
                `Please contact support@ebenezerbooks.com with your payment ID.`
          );
        }
      },

      prefill: { name: address.name, contact: phone, email: address.email || "" },
      notes:   { address: `${address.line1}${address.line2 ? ", " + address.line2 : ""}, ${address.city}, ${address.state} - ${address.pincode}` },
      theme:   { color: "#B07D1C" },
      modal:   { ondismiss: () => setProcessing(false) },
    };

    try {
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (r) => {
        setProcessing(false);
        setErrMsg("Payment failed: " + (r?.error?.description || "Please try again."));
      });
      rzp.open();
    } catch {
      setProcessing(false);
      setErrMsg("Unable to start checkout. Please try again.");
    }
  };

  const isDisabled = disabled || cart.length === 0 || processing;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      <button
        onClick={handlePayment}
        disabled={isDisabled}
        style={{
          width: "100%", padding: "14px 24px",
          background: isDisabled
            ? "var(--dust)"
            : "linear-gradient(135deg, var(--gold) 0%, var(--gold-2) 100%)",
          color: isDisabled ? "var(--muted)" : "#fff",
          border: "none", borderRadius: "var(--radius-full)",
          fontFamily: "var(--font-body)", fontSize: "0.95rem", fontWeight: 700,
          cursor: isDisabled ? "not-allowed" : "pointer",
          boxShadow: isDisabled ? "none" : "var(--shadow-gold)",
          transition: "all 0.2s",
          display: "flex", alignItems: "center", justifyContent: "center", gap: "8px",
        }}
        onMouseEnter={(e) => { if (!isDisabled) { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "var(--shadow-gold-lg)"; } }}
        onMouseLeave={(e) => { if (!isDisabled) { e.currentTarget.style.transform = ""; e.currentTarget.style.boxShadow = "var(--shadow-gold)"; } }}
      >
        {processing
          ? <><Loader2 size={16} style={{ animation: "spin 0.9s linear infinite" }} /> Processing…</>
          : <><Lock size={15} /> Pay ₹{(payable / 100).toFixed(0)}</>
        }
      </button>

      {errMsg && (
        <div style={{
          fontSize: "0.76rem", color: "var(--rust)",
          background: "var(--error-faint)", border: "1px solid var(--rust)",
          borderRadius: "var(--radius)", padding: "8px 12px", lineHeight: 1.55,
        }}>
          {errMsg}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
